import { mergeProps } from '@base-ui/react/merge-props'
import { useRender } from '@base-ui/react/use-render'
import { cva, type VariantProps } from 'class-variance-authority'
import { ArchiveIcon, CircleX } from 'lucide-react'

import { getLoadStatusDotClass } from '../status-colors'
import { cn } from '@repo/ui/lib/utils'

const statusBase = 'border-border bg-background text-black dark:text-white'
const statusDot = 'before:size-3 before:shrink-0 before:rounded-full before:content-[""]'

/** Ghost status badge with a painted dot, e.g. `dot('before:bg-green-600')`. */
const dot = (color: string, extra?: string) =>
  [statusBase, statusDot, color, extra].filter(Boolean).join(' ')

const amberDot = dot('before:bg-amber-500')
const loadStatusDot = (alias: string) => dot(getLoadStatusDotClass(alias) ?? '')

const badgeVariants = cva(
  'group/badge inline-flex h-5 w-fit shrink-0 items-center justify-center gap-2 overflow-hidden border border-transparent px-2 py-0.5 text-xs font-semibold whitespace-nowrap transition-all focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 [&>svg]:pointer-events-none [&>svg]:size-3!',
  {
    defaultVariants: {
      rounding: 'default',
      variant: 'default',
    },
    variants: {
      rounding: {
        default: 'rounded-4xl',
        status: 'rounded-[8px]',
      },
      variant: {
        archived: `${statusBase}`,
        arrived_at_collection: loadStatusDot('arrived_at_collection'),
        arrived_at_destination: loadStatusDot('arrived_at_destination'),
        cancelled: statusBase,
        check_rate: loadStatusDot('check_rate'),
        completed: loadStatusDot('completed'),
        converted_to_load: loadStatusDot('converted_to_load'),
        default: 'bg-primary text-primary-foreground [a]:hover:bg-primary/80',
        destructive:
          'bg-destructive/10 text-destructive focus-visible:ring-destructive/20 dark:bg-destructive/20 dark:focus-visible:ring-destructive/40 [a]:hover:bg-destructive/20',
        draft: amberDot,
        ghost: 'hover:bg-muted hover:text-muted-foreground dark:hover:bg-muted/50',
        in_transit: loadStatusDot('in_transit'),
        link: 'text-primary underline-offset-4 hover:underline',
        loading_started: loadStatusDot('loading_started'),
        need_details: loadStatusDot('need_details'),
        new_inquiry: loadStatusDot('new_inquiry'),
        outline: 'border-border text-foreground [a]:hover:bg-muted [a]:hover:text-muted-foreground',
        payment_validation: loadStatusDot('payment_validation'),
        quoted: loadStatusDot('quoted'),
        secondary: 'bg-secondary text-secondary-foreground [a]:hover:bg-secondary/80',
        to_cover: loadStatusDot('to_cover'),
        transport_planning: loadStatusDot('transport_planning'),
        unloading_confirmed: loadStatusDot('unloading_confirmed'),
        waiting_for_arrival_confirmation: loadStatusDot('waiting_for_arrival_confirmation'),
        waiting_for_docs: loadStatusDot('waiting_for_docs'),
        waiting_for_transit: loadStatusDot('waiting_for_transit'),
      },
    },
  },
)

function Badge({
  children,
  className,
  render,
  rounding = 'default',
  variant = 'default',
  ...props
}: useRender.ComponentProps<'span'> & VariantProps<typeof badgeVariants>) {
  return useRender({
    defaultTagName: 'span',
    props: mergeProps<'span'>(
      {
        children:
          variant === 'archived' ? (
            <>
              <ArchiveIcon data-icon="inline-start" />
              {children}
            </>
          ) : variant === 'cancelled' ? (
            <>
              <CircleX data-icon="inline-start" />
              {children}
            </>
          ) : (
            children
          ),
        className: cn(badgeVariants({ rounding, variant }), className),
      },
      props,
    ),
    render,
    state: {
      rounding,
      slot: 'badge',
      variant,
    },
  })
}

export { Badge, badgeVariants }
